import { useState, useRef, useCallback } from "react";
import { getImageSrcFromItem } from "../utils";

/**
 * Manages the image/video stack: list state, edited item, expand/collapse, add/remove/replace.
 * Used by the main CameraCapture orchestrator.
 */
export function useImageStack() {
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [editedImage, setEditedImage] = useState(null);
  const [stackExpanded, setStackExpanded] = useState(false);
  const [stackClosing, setStackClosing] = useState(false);

  const editedIndexRef = useRef(null);
  const replaceAtIndexRef = useRef(null);
  const imageFilesRef = useRef([]);
  imageFilesRef.current = imageFiles;

  const closeStack = useCallback(() => {
    setStackClosing((prev) => {
      if (prev || !stackExpanded) return prev;
      return true;
    });
  }, [stackExpanded]);

  const handleStackAnimationEnd = useCallback(() => {
    setStackClosing((prev) => {
      if (!prev) return prev;
      setStackExpanded(false);
      return false;
    });
  }, []);

  const addImage = useCallback((buffer) => {
    setImageFiles((prev) => [buffer, ...prev]);
    setStackExpanded(false);
  }, []);

  const replaceImageAt = useCallback((index, buffer, options = {}) => {
    const { preserveLabel = true } = options;
    setImageFiles((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      const existing = prev[index];
      const hadLabel =
        preserveLabel && typeof existing === "object" && existing != null && existing.label != null;
      next[index] = hadLabel ? { image: buffer, label: existing.label } : buffer;
      return next;
    });
    setEditedImage({ image: buffer, index });
  }, []);

  const removeImage = useCallback((indexToRemove) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  }, []);

  const removeVideo = useCallback((indexToRemove) => {
    setVideoFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  }, []);

  const addVideo = useCallback((file) => {
    setVideoFiles((prev) => [...prev, file]);
  }, []);

  const setEditedImageFromIndex = useCallback((index) => {
    editedIndexRef.current = index;
    const item = imageFilesRef.current[index];
    const imageSrc = getImageSrcFromItem(item);
    setEditedImage({ image: imageSrc, index });
  }, []);

  const updateImageAtEditedIndex = useCallback((imageFile) => {
    const index = editedIndexRef.current;
    setImageFiles((prev) => {
      if (index == null || index < 0 || index >= prev.length) return prev;
      const next = [...prev];
      next[index] = imageFile;
      return next;
    });
    editedIndexRef.current = null;
    setEditedImage(null);
  }, []);

  const handleEditorCancel = useCallback(() => {
    replaceAtIndexRef.current = null;
    setEditedImage(null);
  }, []);

  const startRecapture = useCallback(() => {
    const index = editedIndexRef.current;
    if (index != null && index >= 0) {
      replaceAtIndexRef.current = index;
      setEditedImage(null);
    }
  }, []);

  const reset = useCallback(() => {
    setImageFiles([]);
    setVideoFiles([]);
    setStackExpanded(false);
    setStackClosing(false);
    setEditedImage(null);
    replaceAtIndexRef.current = null;
    editedIndexRef.current = null;
  }, []);

  return {
    imageFiles,
    setImageFiles,
    videoFiles,
    setVideoFiles,
    editedImage,
    setEditedImage,
    stackExpanded,
    setStackExpanded,
    stackClosing,
    closeStack,
    handleStackAnimationEnd,
    addImage,
    replaceImageAt,
    removeImage,
    removeVideo,
    addVideo,
    setEditedImageFromIndex,
    updateImageAtEditedIndex,
    handleEditorCancel,
    startRecapture,
    reset,
    editedIndexRef,
    replaceAtIndexRef,
  };
}
